import React, { useEffect, useState, useRef, Component } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { GraduationCapIcon, HandHeartIcon, BriefcaseIcon, MailIcon, ShieldCheckIcon, ClockIcon, LockIcon, MapPinIcon, PhoneIcon, CheckIcon, Building2Icon } from 'lucide-react';
import { Container } from '../components/ui/Container';
import { Button } from '../components/ui/Button';
import { GeometricAccent } from '../components/ui/GeometricAccent';
import { PatternDecor } from '../components/ui/PatternDecor';
import { MosqueArchMask } from '../components/ui/MosqueArchMask';
import { ObfuscatedEmail } from '../components/ui/ObfuscatedEmail';
const quickRoutes = [{
  icon: MailIcon,
  label: 'General Inquiry',
  desc: 'Questions, info, or partnerships — we respond within 48 hours.'
}, {
  icon: GraduationCapIcon,
  label: 'Become a Teacher',
  desc: 'Teaching, mentoring, and educator pathways.'
}, {
  icon: HandHeartIcon,
  label: 'Volunteers',
  desc: 'Support our programs as a community volunteer.'
}, {
  icon: BriefcaseIcon,
  label: 'Careers',
  desc: 'Open roles for educators, designers, and operators.'
}];
const routeDetails: Record<string, {
  eyebrow: string;
  eyebrowIcon: ComponentType<{
    size?: number;
    strokeWidth?: number;
  }>;
  title: string;
  intro: string;
  firstHeading: string;
  firstList: string[];
  secondHeading: string;
  secondList: string[];
  footer: string;
  ctaLabel: string;
}> = {
  'Become a Teacher': {
    eyebrow: 'BECOME A TEACHER',
    eyebrowIcon: GraduationCapIcon,
    title: 'Join Our Educational Mission',
    intro: 'We collaborate with qualified educators and subject experts who share our commitment to ethical, culturally sensitive education.',
    firstHeading: 'Teachers at International Islamic Learning Hub contribute to:',
    firstList: ['child and adult education programs', 'online and hybrid learning formats', 'value-based, inclusive educational environments'],
    secondHeading: 'We welcome professionals with experience in:',
    secondList: ['education and pedagogy', 'languages and cultural studies', 'family and community education'],
    footer: 'All teaching roles are aligned with our safeguarding, ethical, and quality standards.',
    ctaLabel: 'Apply to Become a Teacher'
  },
  Volunteers: {
    eyebrow: 'VOLUNTEERS',
    eyebrowIcon: HandHeartIcon,
    title: 'Support Families. Strengthen Communities.',
    intro: 'Volunteers play an important role in supporting our community-focused initiatives.',
    firstHeading: 'Volunteer opportunities may include:',
    firstList: ['community support and coordination', 'educational assistance', 'translation and content support', 'outreach and communication'],
    secondHeading: 'Volunteering with us is an opportunity to:',
    secondList: ['contribute meaningfully', 'support Muslim families living as minorities', 'gain experience in an international, mission-driven environment'],
    footer: '',
    ctaLabel: 'Become a Volunteer'
  },
  Careers: {
    eyebrow: 'CAREERS',
    eyebrowIcon: BriefcaseIcon,
    title: 'Professional Roles with Purpose',
    intro: 'We offer professional opportunities for individuals seeking meaningful work aligned with education, social impact, and ethical responsibility.',
    firstHeading: 'Career opportunities may include roles in:',
    firstList: ['program coordination', 'educational delivery', 'administration and operations', 'partnerships and project management'],
    secondHeading: 'We value:',
    secondList: ['professionalism', 'cultural awareness', 'integrity and responsibility'],
    footer: 'Positions may be full-time, part-time, or project-based, depending on program needs and funding.',
    ctaLabel: 'View Current Opportunities'
  },
  'General Inquiry': {
    eyebrow: 'CONTACT',
    eyebrowIcon: MailIcon,
    title: 'Get in Touch',
    intro: '',
    firstHeading: 'We welcome inquiries from:',
    firstList: ['parents and families', 'educators and professionals', 'partners and institutions', 'donors and foundations'],
    secondHeading: 'Please contact us for:',
    secondList: ['general questions', 'partnership discussions', 'program information', 'scholarships'],
    footer: 'All messages are handled with care and confidentiality.',
    ctaLabel: 'Contact Us'
  }
};
const hashToRoute: Record<string, string> = {
  '#become-a-teacher': 'Become a Teacher',
  '#volunteers': 'Volunteers',
  '#careers': 'Careers',
  '#general-inquiry': 'General Inquiry'
};
type FormData = {
  name: string;
  email: string;
  country: string;
  role: string;
  message: string;
};
type FormErrors = Partial<Record<keyof FormData | 'consent', string>>;
const emptyForm: FormData = {
  name: '',
  email: '',
  country: '',
  role: '',
  message: ''
};
const WEB3FORMS_ACCESS_KEY = 'e3cb5ab6-527c-4e33-a0c2-8cb9b536889b';
export function Contact() {
  const [route, setRoute] = useState(quickRoutes[0].label);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const honeypotRef = useRef<HTMLInputElement>(null);
  const updateField = (field: keyof FormData) => (value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setErrors(prev => {
      if (!prev[field]) return prev;
      const next = {
        ...prev
      };
      delete next[field];
      return next;
    });
  };
  const validate = (data: FormData, hasConsent: boolean): FormErrors => {
    const next: FormErrors = {};
    if (!data.name.trim()) next.name = 'Please enter your name.';
    if (!data.email.trim()) {
      next.email = 'Please enter your email.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim())) {
      next.email = 'Please enter a valid email address.';
    }
    if (!data.message.trim()) next.message = 'Please enter a message.';
    if (!hasConsent) next.consent = 'Please confirm you agree before sending.';
    return next;
  };
  const location = useLocation();
  useEffect(() => {
    const hash = location.hash;
    if (hash && hashToRoute[hash]) {
      setRoute(hashToRoute[hash]);
      setTimeout(() => {
        const el = document.getElementById('contact-routes');
        if (el) el.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  }, [location.hash]);
  return <>
      {/* Hero */}
      <section className="relative overflow-hidden isolate" data-id="element-3492" data-mp="14z493pr20vqw">
        <PatternDecor side="right" y="middle" size="lg" data-id="element-3493" data-mp="14znlkqxxqrxj" />
        <GeometricAccent variant="dots" className="absolute top-10 left-6 w-32 h-32" opacity={0.4} data-id="element-3494" data-mp="19yqljwz267yn" />
        <Container className="relative py-16 md:py-24" data-id="element-3495" data-mp="v0euq6yd9tfe">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center" data-id="element-3496" data-mp="zzhvvgb87fi3">
            <div className="lg:col-span-6" data-id="element-3497" data-mp="m8mmm2wtewdz">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sage-100 text-forest-700 text-[11px] font-semibold tracking-[0.14em] uppercase" data-id="element-3498" data-mp="m7up2mjnqol2">
                Contact
              </span>
              <h1 className="font-display text-5xl md:text-7xl text-ink-900 leading-[1.05] mt-5" data-id="element-3499" data-mp="cc9tb656mkjr">
                Get In Touch
              </h1>
              <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-3500" data-mp="3a0lio1at5a2">
                Whether you're a parent, partner, educator, or supporter — we're
                here to listen. Choose the path that fits you best, and our team
                will respond promptly.
              </p>
            </div>
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            ease: 'easeOut'
          }} className="lg:col-span-6 relative" data-id="element-3501" data-mp="is1mdqdnfmsr">
              <div className="relative w-full lg:w-[calc(100%+6rem)] lg:-ml-8 aspect-[200/148]" data-id="element-3502" data-mp="1k5cj26ifn8">
                <MosqueArchMask src="https://cdn.magicpatterns.com/uploads/ehqbib2hEnjeCwyz1Hvru2/Contact7..jpg" alt="Leather IILH-branded journal on a wooden desk beside globes, scholarly books, and a brass pen, with a city view through the window" data-id="element-3503" data-mp="3r7btjcwebi7e" />
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Quick routes */}
      <section id="contact-routes" className="pb-12 scroll-mt-24 bg-sage-50/60" data-id="element-3504" data-mp="1sqjdavz3v3b4">
        <Container data-id="element-3505" data-mp="1wr3eqbqpot32">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-3506" data-mp="1wqbrbo79q1nv">
            {quickRoutes.map((r, i) => {
            const active = route === r.label;
            return <motion.button key={r.label} type="button" onClick={() => setRoute(r.label)} initial={{
              opacity: 0,
              y: 12
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.4,
              delay: i * 0.06
            }} className={`text-left p-5 rounded-2xl border transition-all ${active ? 'bg-forest-700 border-forest-700 text-ivory shadow-card' : 'bg-white border-sage-200 text-ink-900 hover:border-forest-700/30'}`} data-id="element-3507" data-mp="26q2ndubh8oq2" data-mpi={i}>
                  <span className={`w-10 h-10 inline-flex items-center justify-center rounded-full ${active ? 'bg-ivory/15 text-ivory' : 'bg-sage-100 text-forest-700'}`} data-id="element-3508" data-mp="tuubmr5vp0jw">
                    <r.icon size={18} strokeWidth={1.6} data-id="element-3509" data-mp="13ss79zq2hzmj" />
                  </span>
                  <p className="mt-4 font-semibold text-sm tracking-[0.06em] uppercase" data-id="element-3510" data-mp="68nqr4bcvqc9">
                    {r.label}
                  </p>
                  <p className={`mt-2 text-xs ${active ? 'text-ivory/70' : 'text-ink-500'}`} data-id="element-3511" data-mp="g5k4bog432no">
                    {r.desc}
                  </p>
                </motion.button>;
          })}
          </div>
        </Container>
      </section>

      {/* Description + form */}
      <section className="relative py-12 md:py-16 overflow-hidden isolate scroll-mt-24 bg-white/60" data-id="element-3512" data-mp="3okamjaasj9es">
        <PatternDecor side="left" y="bottom" size="md" data-id="element-3513" data-mp="25oxzc9m8l8lq" />
        <Container data-id="element-3514" data-mp="25nmtm498dqrz">
          <div className="grid lg:grid-cols-12 gap-12 items-start" data-id="element-3515" data-mp="21nuknqfxdim3">
            {/* Descriptive column */}
            <div className="lg:col-span-6" data-id="element-3516" data-mp="1wohyl7wvofkk">
              <AnimatePresence mode="wait" data-id="element-3517" data-mp="1wngcm8ebq8o2">
                {(() => {
                const detail = routeDetails[route];
                if (!detail) return null;
                const EyebrowIcon = detail.eyebrowIcon;
                return <motion.div key={route} initial={{
                  opacity: 0,
                  y: 12
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -8
                }} transition={{
                  duration: 0.35,
                  ease: 'easeOut'
                }} data-id="element-3518" data-mp="1i04z40yl0elb">
                      <div className="inline-flex items-center gap-2 text-forest-700" data-id="element-3519" data-mp="ud9rksrehtn5">
                        <EyebrowIcon size={18} strokeWidth={1.8} data-id="element-3520" data-mp="gdqjfzylaiau" />
                        <span className="text-[11px] font-semibold tracking-[0.18em] uppercase" data-id="element-3521" data-mp="gfl6pkxc2mi3">
                          {detail.eyebrow}
                        </span>
                      </div>
                      <h2 className="mt-3 font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-3522" data-mp="186qzfgaq5hxc">
                        {detail.title}
                      </h2>
                      {detail.intro ? <p className="mt-5 text-ink-500 leading-relaxed" data-id="element-3523" data-mp="13doe1vg3pes8">
                          {detail.intro}
                        </p> : null}

                      <p className="mt-7 text-ink-500 font-semibold" data-id="element-3524" data-mp="1r15zzacopina">
                        {detail.firstHeading}
                      </p>
                      <ul className="mt-3 space-y-2.5" data-id="element-3525" data-mp="3gjw52iwj1oik">
                        {detail.firstList.map(item => <li key={item} className="flex items-start gap-2.5 text-ink-700" data-id="element-3526" data-mp="3bk9tedwckgbv">
                            <CheckIcon size={16} className="text-forest-700 mt-1 flex-shrink-0" strokeWidth={2.2} data-id="element-3527" data-mp="3lbe4h2iwjh1f" />
                            {item}
                          </li>)}
                      </ul>

                      <p className="mt-7 text-ink-500 font-semibold" data-id="element-3528" data-mp="37l10fb94smhd">
                        {detail.secondHeading}
                      </p>
                      <ul className="mt-3 space-y-2.5" data-id="element-3529" data-mp="mt8o5t4y459x">
                        {detail.secondList.map(item => <li key={item} className="flex items-start gap-2.5 text-ink-700" data-id="element-3530" data-mp="hv72d0g4xrur">
                            <CheckIcon size={16} className="text-forest-700 mt-1 flex-shrink-0" strokeWidth={2.2} data-id="element-3531" data-mp="2shhosvy9zrol" />
                            {item}
                          </li>)}
                      </ul>

                      {detail.footer ? <p className="mt-7 text-ink-500 leading-relaxed" data-id="element-3532" data-mp="3bgn87sncu972">
                          {detail.footer}
                        </p> : null}
                    </motion.div>;
              })()}
              </AnimatePresence>
            </div>

            {/* Form column */}
            <div className="lg:col-span-6" data-id="element-3533" data-mp="3p8tdgjoyyvrq">
              <form noValidate className="rounded-3xl bg-white border border-sage-200 p-7 md:p-10 shadow-soft" onSubmit={async e => {
              e.preventDefault();
              const validationErrors = validate(formData, consent);
              if (Object.keys(validationErrors).length > 0) {
                setErrors(validationErrors);
                setSubmitStatus('idle');
                return;
              }
              // Honeypot — if filled, silently treat as success (likely a bot)
              if (honeypotRef.current?.value) {
                setSubmitStatus('success');
                setFormData(emptyForm);
                setConsent(false);
                return;
              }
              setErrors({});
              setIsSubmitting(true);
              setSubmitStatus('idle');
              try {
                const response = await fetch('https://api.web3forms.com/submit', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json'
                  },
                  body: JSON.stringify({
                    access_key: WEB3FORMS_ACCESS_KEY,
                    subject: `New ${route} message from ${formData.name.trim()}`,
                    from_name: 'IILH Website — Contact Form',
                    inquiry_type: route,
                    name: formData.name.trim(),
                    email: formData.email.trim(),
                    country: formData.country.trim(),
                    role: formData.role.trim(),
                    message: formData.message.trim(),
                    consent_given: 'Yes — agreed to Privacy Policy'
                  })
                });
                const result = await response.json();
                if (result.success) {
                  setSubmitStatus('success');
                  setFormData(emptyForm);
                  setConsent(false);
                } else {
                  setSubmitStatus('error');
                }
              } catch {
                setSubmitStatus('error');
              } finally {
                setIsSubmitting(false);
              }
            }} data-id="element-3534" data-mp="3p9uxvervkum4">
                {/* Honeypot — hidden from real users, catches bots */}
                <input ref={honeypotRef} type="text" name="botcheck" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" data-id="element-3535" data-mp="28ub2iic7p14l" />
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-forest-700 mb-1" data-id="element-3536" data-mp="sf9rj2wcoozm">
                  {route}
                </p>
                <h2 className="font-display text-2xl md:text-3xl text-ink-900" data-id="element-3537" data-mp="nhzv33ajnd2a">
                  Send us a message
                </h2>
                <p className="mt-2 text-ink-500 text-sm" data-id="element-3538" data-mp="6mcigfu9zu99">
                  All fields are confidential.
                </p>

                <div className="mt-6 grid sm:grid-cols-2 gap-4" data-id="element-3539" data-mp="bcj6oekvlcqz">
                  <Field label="Full name" placeholder="Your full name" required value={formData.name} onChange={updateField('name')} error={errors.name} data-id="element-3540" data-mp="bff3u0owfxed" />
                  <Field label="Email" placeholder="you@example.com" type="email" required value={formData.email} onChange={updateField('email')} error={errors.email} data-id="element-3541" data-mp="3jupc5qjdtyul" />
                  <Field label="Country" placeholder="Where you are based" value={formData.country} onChange={updateField('country')} data-id="element-3542" data-mp="190kwz509z18h" />
                  <Field label="Role" placeholder="Parent, educator, donor…" value={formData.role} onChange={updateField('role')} data-id="element-3543" data-mp="2ol3jjqa6kgzn" />
                </div>
                <div className="mt-4" data-id="element-3544" data-mp="29uwd68xg6ofb">
                  <label className="block text-sm font-medium text-ink-900 mb-1.5" data-id="element-3545" data-mp="29vxli5n2wwtc">
                    Message <span className="text-forest-700" data-id="element-3546" data-mp="25s82dvp6b7fp">*</span>
                  </label>
                  <textarea rows={5} placeholder="Share what you need — we read every message." value={formData.message} onChange={e => updateField('message')(e.target.value)} className={`w-full rounded-xl border bg-ivory/40 px-4 py-3 text-[15px] focus:outline-none focus:bg-white transition ${errors.message ? 'border-red-400 focus:border-red-500' : 'border-sage-200 focus:border-forest-700'}`} data-id="element-3547" data-mp="20toh7xielzoe" />
                  {errors.message && <p className="mt-1.5 text-xs text-red-600" data-id="element-3548" data-mp="h0t4ukns3b11">
                      {errors.message}
                    </p>}
                </div>
                {submitStatus === 'success' && <div className="mt-4 p-4 rounded-xl bg-forest-50 border border-forest-200 text-forest-800 text-sm" data-id="element-3549" data-mp="37bkwyr6jwah">
                    ✓ Message sent successfully! We'll respond within 24-48
                    hours.
                  </div>}
                {submitStatus === 'error' && <div className="mt-4 p-4 rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm" data-id="element-3550" data-mp="3kfymxsrjx3du">
                    Failed to send message. Please try again or email us
                    directly at{' '}
                    <ObfuscatedEmail user="info" domain="iilh.org" className="underline" data-id="element-3551" data-mp="1ip532taj6001" />
                  </div>}
                <div className="mt-5" data-id="element-3552" data-mp="1v5d2asvo3jhh">
                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-3553" data-mp="1zxed0ad6t25e">
                    <input type="checkbox" checked={consent} onChange={e => {
                    setConsent(e.target.checked);
                    if (e.target.checked) setErrors(prev => {
                      if (!prev.consent) return prev;
                      const next = {
                        ...prev
                      };
                      delete next.consent;
                      return next;
                    });
                  }} className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-sage-300 text-forest-700 focus:ring-forest-700/40 accent-forest-700" data-id="element-3554" data-mp="1zy66nfn84pt1" />
                    <span className="text-xs text-ink-700 leading-relaxed" data-id="element-3555" data-mp="27vpsgbgb7na7">
                      I consent to International Islamic Learning Hub processing
                      the details I provide in order to respond to my inquiry,
                      in line with the{' '}
                      <Link to="/en/legal/privacy-policy" className="text-forest-700 underline hover:text-forest-800" data-id="element-3556" data-mp="2cvucgbgff056">
                        Privacy Policy
                      </Link>
                      .
                    </span>
                  </label>
                  {errors.consent && <p className="mt-1.5 text-xs text-red-600" data-id="element-3557" data-mp="1u5w4dvcmp71j">
                      {errors.consent}
                    </p>}
                </div>
                <div className="mt-5 flex items-center justify-between flex-wrap gap-4" data-id="element-3558" data-mp="16e733phtx88g">
                  <p className="text-xs text-ink-500 flex items-center gap-2" data-id="element-3559" data-mp="11gfcqzwu1u6d">
                    <LockIcon size={12} data-id="element-3560" data-mp="3d3ilnjzhtakz" /> Your data is encrypted and never
                    shared.
                  </p>
                  <Button variant="primary" type="submit" disabled={isSubmitting} data-id="element-3561" data-mp="3hwbscdeiuszp">
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </Container>
      </section>

      {/* Reassurance cards */}
      <section className="py-12 md:py-16 bg-[#EDF2F7]/60" data-id="element-3562" data-mp="4npgg1am3lj8">
        <Container data-id="element-3563" data-mp="3muveadt484s2">
          <div className="grid md:grid-cols-3 gap-5" data-id="element-3564" data-mp="3mtby0n7gltqa">
            <div className="rounded-2xl bg-sage-50 border border-sage-200 p-6" data-id="element-3565" data-mp="3qvfanf5v74d5">
              <span className="w-10 h-10 inline-flex items-center justify-center rounded-full bg-white text-forest-700" data-id="element-3566" data-mp="3qxjnwnpnlfq3">
                <ShieldCheckIcon size={18} strokeWidth={1.6} data-id="element-3567" data-mp="2fga8cc9nb7u2" />
              </span>
              <h3 className="mt-4 font-display text-lg text-ink-900" data-id="element-3568" data-mp="2vnofr1pqypa1">
                Confidential by default
              </h3>
              <p className="mt-2 text-ink-700 text-sm leading-relaxed" data-id="element-3569" data-mp="2qwojlyogcb8l">
                All communications are private. We follow strict safeguarding
                standards — especially for inquiries involving children,
                schools, or discrimination.
              </p>
            </div>
            <div className="rounded-2xl bg-sand-50 border border-sand-200 p-6" data-id="element-3570" data-mp="3emsmg3ttg6yj">
              <span className="w-10 h-10 inline-flex items-center justify-center rounded-full bg-white text-sand-500" data-id="element-3571" data-mp="39nphejdu60qk">
                <ClockIcon size={18} strokeWidth={1.6} data-id="element-3572" data-mp="34nl9hhqzueby" />
              </span>
              <h3 className="mt-4 font-display text-lg text-ink-900" data-id="element-3573" data-mp="30n1cce6f7apt">
                Response time
              </h3>
              <p className="mt-2 text-ink-700 text-sm leading-relaxed" data-id="element-3574" data-mp="1t9g2efvhhoce">
                Most messages are reviewed within 24–48 hours, Monday to Friday.
                Sensitive cases are prioritized.
              </p>
            </div>
            <div className="rounded-2xl bg-white border border-sage-200 p-6" data-id="element-3575" data-mp="4reo67dfiw0d">
              <span className="w-10 h-10 inline-flex items-center justify-center rounded-full bg-white border border-sage-200 text-forest-700" data-id="element-3576" data-mp="8ryiz4aj5njc">
                <Building2Icon size={18} strokeWidth={1.6} data-id="element-3577" data-mp="dofa87d10i0n" />
              </span>
              <h3 className="mt-4 font-display text-lg text-ink-900" data-id="element-3578" data-mp="ij1kg77dg4j5">
                Headquarters
              </h3>
              <ul className="mt-3 space-y-3 text-ink-700 text-sm" data-id="element-3579" data-mp="nie8uwdw5jnu">
                <li className="flex items-start gap-3" data-id="element-3580" data-mp="sdl73ooe5w5d">
                  <MapPinIcon size={16} className="text-forest-700 mt-0.5 flex-shrink-0" strokeWidth={1.6} data-id="element-3581" data-mp="wg82u0o6o17u" />
                  Aia tn 10, Tallinn, 10111 Estonia
                </li>
                <li className="flex items-start gap-3" data-id="element-3582" data-mp="2ya7cyipkkciy">
                  <PhoneIcon size={16} className="text-forest-700 mt-0.5 flex-shrink-0" strokeWidth={1.6} data-id="element-3583" data-mp="3388rcqc1vno3" />
                  <a href="tel:+37258888275" className="hover:text-forest-700 transition" data-id="element-3584" data-mp="5ee4denbndy0">
                    +372 5888 8275
                  </a>
                </li>
                <li className="flex items-start gap-3" data-id="element-3585" data-mp="ediz09vrin4w">
                  <MailIcon size={16} className="text-forest-700 mt-0.5 flex-shrink-0" strokeWidth={1.6} data-id="element-3586" data-mp="tbbhnawlvrnb" />
                  <ObfuscatedEmail user="info" domain="iilh.org" className="hover:text-forest-700 transition" data-id="element-3587" data-mp="123vi8gwgk6as" />
                </li>
              </ul>
            </div>
          </div>
        </Container>
      </section>
    </>;
}
function Field({
  label,
  placeholder,
  type = 'text',
  name,
  required = false,
  value,
  onChange,
  error
}: {
  label: string;
  placeholder?: string;
  type?: string;
  name?: string;
  required?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
}) {
  return <div data-id="element-3588" data-mp="1gqu8i8jkwunx">
      <label className="block text-sm font-medium text-ink-900 mb-1.5" data-id="element-3589" data-mp="1gr3msyv9hdds">
        {label}
        {required && <span className="text-forest-700" data-id="element-3590" data-mp="1lp5gedqbcv3i"> *</span>}
      </label>
      <input type={type} name={name} placeholder={placeholder} value={value} onChange={onChange ? e => onChange(e.target.value) : undefined} className={`w-full rounded-xl border bg-ivory/40 px-4 py-3 text-[15px] focus:outline-none focus:bg-white transition ${error ? 'border-red-400 focus:border-red-500' : 'border-sage-200 focus:border-forest-700'}`} data-id="element-3591" data-mp="1lrhxyps5t3g1" />
      {error && <p className="mt-1.5 text-xs text-red-600" data-id="element-3592" data-mp="ozo4hh0qmctd">{error}</p>}
    </div>;
}